import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LayoutDashboard, Package, ShoppingBag, Tag, Settings, Inbox, Users, LogOut, ArrowLeft, Truck, Radio, Map, KeyRound, UserCog, Layout as LayoutIcon, Megaphone, Mail, Boxes, Wallet, Warehouse, Building2, ShieldCheck, Package2, ListChecks, Ship, ClipboardList, Timer, BriefcaseBusiness, Coffee, PackagePlus, ArrowUpDown, CheckCircle2, Barcode, Layers, CalendarCheck, ClipboardCheck, FileSearch, Database, AlertTriangle, Wrench, MapPin, Presentation } from 'lucide-react';
import { useAdminAuth, API } from '../context/AdminAuthContext';
import NotificationsBell from './NotificationsBell';
import BroadcastButton from './BroadcastButton';
import PasskeyEnrollCard from './PasskeyEnrollCard';
import { DutyGate } from './OffDutyScreen';
import AttendanceDialog from './AttendanceDialog';
import MobileNav from './MobileNav';

const AdminLayout = ({ children }) => {
  const { token, logout, email, authHeaders, warehouses, warehouseScopeAll } = useAdminAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [newInquiries, setNewInquiries] = useState(0);
  const [pendingDrivers, setPendingDrivers] = useState(0);
  // RBAC profile — null until /admin/me responds. While null we render
  // a minimal layout (no menu) so we don't briefly leak hidden routes.
  const [me, setMe] = useState(null);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    axios.get(`${API}/admin/me`, { headers: authHeaders })
      .then(({ data }) => { if (alive) setMe(data); })
      .catch(() => { if (alive) setMe({ permissions: [], is_super_admin: false }); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    const fetchCount = async () => {
      try {
        const [inq, drv] = await Promise.all([
          axios.get(`${API}/admin/inquiries/count`, { headers: authHeaders }).catch(() => null),
          axios.get(`${API}/admin/drivers/pending-count`, { headers: authHeaders }).catch(() => null),
        ]);
        if (!alive) return;
        if (inq) setNewInquiries(inq.data.new || 0);
        if (drv) setPendingDrivers(drv.data.pending || 0);
      } catch {
        // silently ignore
      }
    };
    fetchCount();
    // Auto-refresh disabled per user request (badges only update on
    // route changes; live data only on the Live Stock report).
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, location.pathname]);

  // Feb 2026 — Toggle a body-level `admin-scope` class so the
  // global mobile CSS in index.css can lift every admin page's
  // mobile experience (bigger inputs, sticky bottoms, horizontal
  // table scrolling, tighter card shadows) without touching each
  // page individually. Desktop styling is untouched.
  // MUST sit before ANY early return so React's hook order is
  // consistent between renders.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    document.body.classList.add('admin-scope');
    return () => document.body.classList.remove('admin-scope');
  }, []);

  if (!token) return <Navigate to="/admin/login" replace />;

  const navAll = [
    { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true, perm: 'dashboard' },
    { to: '/admin/categories', label: 'Divisions', icon: Tag, perm: 'categories' },
    { to: '/admin/products', label: 'Products', icon: Package, requireAny: ['products.view'] },
    { to: '/admin/stock', label: 'Stock', icon: Boxes, requireAny: ['products.view_stock', 'products.stock_upload', 'products.stock_download', 'products.stock_report'] },
    // Feb 2026 — Goods Receiving (ASN) module. Captures inbound
    // shipments (vehicle #, expected delivery, line items with prod/
    // exp dates, Hold Pallet toggle) BEFORE the truck arrives. On
    // "Receive" the ASN spawns Stage placements + put-away tasks the
    // picker executes to place stock into permanent locations —
    // production/expiry dates carry through so FEFO stays healthy.
    // Gated by the same warehouse RBAC slugs as Warehouses.
    { to: '/admin/goods-receiving', label: 'Goods Receiving', icon: PackagePlus, requireAny: ['asn', 'asn.view', 'asn.create', 'asn.edit', 'asn.receive', 'asn.assign_putaway', 'warehouse', 'warehouse.view', 'warehouse.edit'] },
    // Feb 2026 — ASN Release-Hold Audit. READ-ONLY surface for the
    // super admin to review the stock-ledger entries created by
    // every `release_asn_hold` call since the start date (default
    // 2026-10-05). Pre-fix duplicate pairs are flagged with a
    // proposed correction. `superOnly` so sub-admins never see the
    // link, and `requireExact: null` so even super admins only land
    // via the explicit menu entry. Backend enforces the same gate.
    { to: '/admin/release-hold-audit', label: 'Release-Hold Audit', icon: FileSearch, perm: null, superOnly: true },
    // Feb 2026 — Backup Stock Data. READ-ONLY one-click download of
    // the current stock ledger + product batches + placements. The
    // server stores nothing; the "last backup" timestamp lives in
    // the browser's localStorage.
    { to: '/admin/backup-stock-data', label: 'Backup Stock Data', icon: Database, perm: null, superOnly: true },
    // Aug 2026 — Warehouses (and its child Floor / Pick / Stock views)
    // are STRICTLY hidden from users who don't hold an explicit
    // `warehouse` or `floor` slug. Super admins bypass as always. This
    // stops `orders.export_only` holders (and anyone with unrelated
    // module grants like `products.view`) from seeing the sidebar
    // entry, and — because AdminLayout uses this list to derive the
    // route guard's allow-list — blocks direct navigation to
    // /admin/warehouses too.
    { to: '/admin/warehouses', label: 'Warehouses', icon: Warehouse, requireAny: ['warehouse', 'warehouse.view', 'warehouse.edit', 'warehouse.stock_check', 'warehouse.stock_adjust', 'warehouse.stock_adjust_request', 'warehouse.stock_adjust_approve', 'floor', 'floor.view'] },
    { to: '/admin/pallets', label: 'Pallet Ledger', icon: Package2, requireExact: 'pallets.manage' },
    // Feb 2026 — Stock Adjustment console. Gated on the exact
    // `warehouse.stock_adjust` slug so super admins always see it and
    // sub-admins only see it when explicitly granted. The slug matches
    // the backend permission gate on POST /admin/warehouse/stock-adjustments.
    { to: '/admin/stock-adjust', label: 'Stock Adjustment', icon: ArrowUpDown, requireAny: ['warehouse.stock_adjust', 'warehouse.stock_adjust_request'] },
    // Feb 2026 — Stock Approvals queue. Approvers (super admin OR
    // `warehouse.stock_adjust_approve`) review pending requests
    // submitted via the Stock Adjustment page's request-flow.
    { to: '/admin/stock-approvals', label: 'Stock Approvals', icon: CheckCircle2, requireExact: 'warehouse.stock_adjust_approve' },
    // Feb 2026 — Audit Queue. Super-admin-only review surface for
    // move requests raised from Floor Mode > Audit. Approving
    // relocates the pallet via `_apply_transfer_stock_move`.
    { to: '/admin/audit-queue', label: 'Audit Queue', icon: ClipboardCheck, perm: null, superOnly: true },
    // Feb 2026 — End-of-day report covering receiving, dispatch,
    // adjustments, orders, payments, and manual ledger entries.
    { to: '/admin/day-close', label: 'Day Close', icon: CalendarCheck, requireAny: ['warehouse.reports'] },
    // Sep 2026 — review list for historical-backfill insufficient-stock rows.
    { to: '/admin/discrepancies', label: 'Ledger Discrepancies', icon: ClipboardList, perm: null, superOnly: true },
    // Feb 2026 — Stock Mismatch Report (Step 5). READ-ONLY diagnostic
    // listing products whose `stock_qty` scalar drifts from the sum of
    // `warehouse_placements.qty`. Super admin only — backend enforces
    // the same gate.
    { to: '/admin/stock-mismatch', label: 'Stock Mismatch', icon: AlertTriangle, perm: null, superOnly: true },
    // Oct 2026 — Stock Reconciliation Tool. Detects the historical
    // ASN double-release bug and writes corrective ledger rows per
    // approved product. Super admin only — backend enforces the gate.
    { to: '/admin/stock-reconciliation', label: 'Stock Reconciliation', icon: Wrench, perm: null, superOnly: true },
    // Oct 2026 — Stock Position Report. Filtered by location range +
    // division; exports to XLSX/PDF with a column picker. Carries the
    // same `products.stock_report` grant as the Live Stock page.
    { to: '/admin/stock-position', label: 'Stock Position', icon: MapPin, perm: 'products.stock_report' },
    // Feb 2026 — Label Printing (Barcode 39). Sits under the warehouse
    // group so it's discoverable next to Stock Adjustment. Gated on
    // the fine-grained `warehouse.label_print` slug; super admin and
    // `warehouse` wildcard implicitly pass.
    { to: '/admin/label-print', label: 'Label Printing', icon: Barcode, requireAny: ['warehouse', 'warehouse.label_print'] },
    // Feb 2026 — Zone Management CRUD. Gated on `warehouse.zones`.
    // Consumed by Floor put-away dropdown + Live Stock Report.
    { to: '/admin/zones', label: 'Zones', icon: Layers, requireAny: ['warehouse', 'warehouse.zones'] },
    // Aug 2026 — the "Orders" sidebar entry now uses `requireAny` so
    // holders of ONLY `orders.export_only` (introduced to grant Export
    // Orders access without unlocking the general branch/local pages)
    // don't accidentally see the general Orders module here. The set
    // matches every "real" orders.* slug the RBAC catalog exposes.
    { to: '/admin/orders', label: 'Orders', icon: ShoppingBag, requireAny: ['orders', 'orders.view', 'orders.create', 'orders.edit_status', 'orders.assign_driver', 'orders.assign_wms_tasks', 'orders.delete'] },
    { to: '/admin/allocations', label: 'Allocation Details', icon: ListChecks, requireAny: ['orders', 'orders.view', 'orders.create', 'orders.edit_status', 'orders.assign_driver', 'orders.assign_wms_tasks', 'orders.delete'] },
    // Aug 2026 — Export Orders is now ALSO reachable via
    // `orders.export_only`. Existing `export.<dept>.edit` grants still
    // work, plus super admins bypass everything as before.
    { to: '/admin/export-dashboard', label: 'Export Orders', icon: Ship, requireAny: ['export', 'export.documentation.edit', 'export.clearing.edit', 'export.transport.edit', 'export.accounts.edit', 'export.logistics.edit', 'orders.export_only'] },
    { to: '/admin/dispatch', label: 'Dispatch', icon: Radio, perm: 'dispatch' },
    { to: '/admin/drivers', label: 'Drivers', icon: Truck, badge: pendingDrivers, perm: 'drivers' },
    { to: '/admin/inquiries', label: 'Inquiries', icon: Inbox, badge: newInquiries, perm: 'marketing' },
    // 2026-10 — WMS marketing enquiries inbox. `wms.contacts_view`
    // (or super admin) unlocks the link; `wms.contacts_manage` is
    // what enables the Mark-read / Archive buttons on the page.
    { to: '/admin/wms-contacts', label: 'WMS Enquiries', icon: Inbox, requireAny: ['wms.contacts_view', 'wms.contacts_manage'] },
    // Feb 2026 — `hideIfLocalCustomersHidden` closes the local
    // Customers page off from any sub-admin the backend flags as
    // "Restrict to Export Customers" OR "Export orders only with no
    // customer-management action". Export customers are still
    // reachable via the Export Orders dashboard.
    { to: '/admin/customers', label: 'Customers', icon: Users, perm: 'customers', hideIfLocalCustomersHidden: true },
    { to: '/admin/outstanding', label: 'Outstanding', icon: Wallet, perm: 'outstanding' },
    // Feb 2026 — super-admin-only presentation deck generator (PDF + PPTX).
    { to: '/admin/performance-report', label: 'Performance Report', icon: Presentation, perm: null, superOnly: true },
    // Broadcast is opt-in: only super-admins see it by default, and
    // sub-admins must be explicitly granted `marketing.broadcast`. We use
    // `requireExact` (no bare-module fallback) so a sub-admin holding
    // just `marketing.view` doesn't get broadcast access by accident.
    { to: '/admin/broadcast', label: 'Broadcast', icon: Megaphone, requireExact: 'marketing.broadcast' },
    { to: '/admin/campaigns', label: 'Email Campaigns', icon: Mail, perm: 'marketing' },
    { to: '/admin/notifications-log', label: 'Dispatch Log', icon: Inbox, perm: null, superOnly: true },
    { to: '/admin/settings', label: 'Homepage', icon: Settings, perm: 'settings' },
    { to: '/admin/layout', label: 'Layout', icon: LayoutIcon, perm: 'settings' },
    { to: '/admin/users', label: 'Admin users', icon: UserCog, perm: 'users', superOnly: true },
    { to: '/admin/attendance', label: 'Attendance log', icon: ClipboardList, perm: null, superOnly: true },
    { to: '/admin/my-work', label: 'My work', icon: BriefcaseBusiness, perm: null },
    { to: '/admin/desk-tracker', label: 'Desk tracker', icon: Coffee, perm: null },
    { to: '/admin/work-tracking', label: 'Work tracking', icon: Timer, requireAny: ['overtime.approve', 'overtime.view_all'], superOnly: false },
    { to: '/admin/branches', label: 'Branches', icon: Building2, perm: null, superOnly: true },
    { to: '/admin/access-activity', label: 'Access & Activity', icon: ShieldCheck, perm: null, superOnly: true },
    { to: '/admin/credentials', label: 'Credentials', icon: KeyRound, perm: null },
    { to: '/admin/sitemap', label: 'Site map', icon: Map, perm: null, superOnly: true },
    { to: '/admin/data-security', label: 'Data security', icon: ShieldCheck, perm: null, superOnly: true },
    { to: '/admin/lockdown', label: 'Lockdown', icon: ShieldCheck, perm: null, superOnly: true },
  ];

  // Filter menu by the current admin's permissions.
  // Rules:
  //  - perm === null → always visible (dashboard, credentials, sitemap)
  //  - Super admin → sees everything
  //  - Sub-admin → visible if they have the bare module slug OR any
  //    `{module}.{action}` slug (granular permissions still grant module
  //    visibility, otherwise a user with only `products.delete` would
  //    never see the Products menu)
  //  - superOnly === true → hidden from sub-admins regardless of perm slug
  const perms = me?.permissions || [];
  const permSet = new Set(perms);
  const isSuper = !!me?.is_super_admin;
  const hasModuleAccess = (mod) => {
    if (permSet.has(mod)) return true;
    const prefix = `${mod}.`;
    return perms.some((p) => p.startsWith(prefix));
  };
  const nav = navAll.filter((item) => {
    if (item.superOnly && !isSuper) return false;
    // Feb 2026 — Local Customers gate. Applies BEFORE the super
    // bypass so it never affects super admins (they never carry the
    // flag), and BEFORE any perm-based checks so the item is hidden
    // regardless of which access rule is used. The backend
    // computes `hide_local_customers` (also true when the user is
    // set to `customers.export_only`), keeping frontend simple.
    if (item.hideIfLocalCustomersHidden && !isSuper && !!me?.hide_local_customers) {
      return false;
    }
    // `requireExact: 'slug'` — STRICTLY requires the exact slug, with no
    // bare-module fallback. Used for permissions that need explicit grant
    // (e.g. broadcast — sub-admins shouldn't auto-inherit via `marketing`).
    if (item.requireExact) {
      if (isSuper || permSet.has('*')) return true;
      return permSet.has(item.requireExact);
    }
    if (isSuper || permSet.has('*')) return true;
    // `requireAny: [slug, slug, ...]` — visible when the admin holds at
    // least one of the listed exact slugs (used for cross-cutting pages
    // like /admin/stock that mix several `products.*` actions).
    if (item.requireAny && item.requireAny.length) {
      // Exact-slug match first…
      if (item.requireAny.some((slug) => permSet.has(slug))) return true;
      // …then legacy bare-module fallback: if the admin holds the bare
      // parent module (e.g. `products`), they implicitly hold all
      // sub-actions (e.g. `products.stock_upload`).
      const parents = new Set(item.requireAny.map((s) => s.split('.', 1)[0]));
      for (const p of parents) {
        if (permSet.has(p)) return true;
      }
      return false;
    }
    if (!item.perm) return true;
    return hasModuleAccess(item.perm);
  });

  // Route guard — if the current URL maps to a feature this admin can't
  // see, bounce them to the first nav entry they DO have access to. We
  // explicitly avoid `<Navigate to="/admin" replace />` here because
  // `/admin` is the Dashboard, and when an admin lacks `dashboard.*`
  // permission that creates an infinite redirect loop (→ white screen).
  if (me) {
    const current = navAll.find((item) =>
      item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to),
    );
    if (current && !nav.includes(current)) {
      const fallback = nav[0]?.to;
      if (fallback && fallback !== location.pathname) {
        return <Navigate to={fallback} replace />;
      }
      // No accessible nav at all — render an empty-state instead of
      // looping. The sub-admin can still log out from the layout below.
    }
  }

  const isActive = (item) =>
    item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to);

  return (
    <DutyGate>
    <div className="min-h-[calc(100vh-4rem)] bg-[#F8FAFC] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[#0F172A] text-white flex-shrink-0 hidden md:flex flex-col">
        <div className="p-6 border-b border-neutral-800">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold text-[#71B7F0] uppercase tracking-widest">Admin</div>
            <NotificationsBell role="admin" authHeaders={authHeaders} variant="dark" />
          </div>
          <div className="text-lg font-extrabold mt-1 font-display">
            Dashboard
          </div>
          <div className="text-xs text-neutral-400 mt-2 truncate">{email}</div>
          {/* Feb 2026 — Warehouse assignment pill(s) surfaced from
              `/admin/me`. Renders the user-facing name(s) so ops know
              at a glance which warehouse(s) they're bound to. Shows
              "All warehouses" for super admin + admin-role users
              whose scope is `["*"]`; hides entirely when the fetch
              is still loading or the user has no warehouse binding. */}
          {Array.isArray(warehouses) && warehouses.length > 0 && (
            <div
              className="mt-2 flex flex-wrap gap-1"
              data-testid="admin-header-warehouses"
              title={warehouseScopeAll
                ? 'Access to every warehouse'
                : `Assigned to: ${warehouses.map((w) => w.name).join(', ')}`}
            >
              {warehouseScopeAll ? (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
                  data-testid="admin-header-warehouse-all"
                >
                  All warehouses ({warehouses.length})
                </span>
              ) : (
                warehouses.slice(0, 3).map((w) => (
                  <span
                    key={w.id}
                    className="inline-flex items-center rounded-full bg-[#0078D4]/25 border border-[#71B7F0]/50 text-[#DCEBFB] px-2 py-0.5 text-[10px] font-semibold"
                    data-testid={`admin-header-warehouse-${w.id}`}
                  >
                    {w.name}
                  </span>
                ))
              )}
              {!warehouseScopeAll && warehouses.length > 3 && (
                <span
                  className="inline-flex items-center rounded-full bg-white/10 border border-white/20 text-neutral-300 px-2 py-0.5 text-[10px] font-semibold"
                  data-testid="admin-header-warehouse-more"
                >
                  +{warehouses.length - 3} more
                </span>
              )}
            </div>
          )}
          {(isSuper || permSet.has('*') || permSet.has('marketing.broadcast')) && (
            <div className="mt-3">
              <BroadcastButton authHeaders={authHeaders} variant="sidebar" />
            </div>
          )}
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {nav.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              data-testid={item.to === '/admin/pallets' ? 'admin-nav-pallets' : undefined}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive(item)
                  ? 'bg-[#0078D4] text-white'
                  : 'text-neutral-300 hover:bg-white/5 hover:text-white'
              }`}
            >
              <item.icon className="w-4 h-4" />
              <span className="flex-1">{item.label}</span>
              {item.badge > 0 && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-500 text-white min-w-[20px] text-center">
                  {item.badge}
                </span>
              )}
            </Link>
          ))}
        </nav>
        <div className="p-3 border-t border-neutral-800 space-y-1">
          <Link
            to="/"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-neutral-300 hover:bg-white/5 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to store
          </Link>
          <button
            onClick={() => {
              logout();
              navigate('/admin/login', { replace: true });
            }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-neutral-300 hover:bg-red-500/10 hover:text-red-300 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </aside>

      {/* Mobile top nav — Feb 2026: replaced the horizontal-scroll
          pill list with a compact bar (brand + bell + dropdown menu).
          Desktop layout above is unchanged; the sidebar handles nav
          on ≥md screens. Curated dropdown links (My Work, Overtime &
          Desk Tracking, Export Orders, Warehouses & FEFO Dispatch)
          keep the mobile header uncluttered; the rest of the admin
          modules remain reachable via direct URL. */}
      <MobileNav navItems={nav} authHeaders={authHeaders} />

      <main className="flex-1 min-w-0 overflow-x-clip md:pt-0 pt-14">
        <div className="px-4 sm:px-6 pt-4">
          <PasskeyEnrollCard role="admin" authHeaders={authHeaders} variant="light" />
        </div>
        {children}
      </main>
    </div>
    <AttendanceDialog />
    </DutyGate>
  );
};

export default AdminLayout;
